import { Modal } from './accessibility.jsx';

const { useEffect, useRef, useState } = React;
const account = () => [PCMAdapter.getRole(), PCMAdapter.getRole() === 'admin'
  ? PCMAdapter.getAdminToken() : PCMAdapter.getToken()];
const sameAccount = (a) => a.every((value, i) => value === account()[i]);

function McpOAuthClients({lang, scope}) {
  const he = lang === 'he';
  const text = (en, hebrew) => he ? hebrew : en;
  const tenant = String(scope);
  const identity = useRef(account());
  const liveScope = useRef(tenant);
  liveScope.current = tenant;
  const alive = useRef(true);
  const epoch = useRef(0);
  const reads = useRef(0);
  const requests = useRef(new Set());
  const locked = useRef(false);
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const heading = useRef(null);
  const errorRef = useRef(null);
  const id = React.useId();
  const current = () => alive.current && liveScope.current === tenant && sameAccount(identity.current);
  const abort = () => { requests.current.forEach(controller => controller.abort()); requests.current.clear(); };
  const close = () => {
    ++epoch.current; abort(); locked.current = false;
    setDialog(null); setBusy(false); setError('');
    load();
  };
  const invalidate = () => {
    ++epoch.current; ++reads.current; abort(); alive.current = false;
    setDialog(null); setData(null); setBusy(false); setError(''); setListError(''); setInvalid(true);
  };
  const load = async () => {
    if (!current()) return;
    const version = ++reads.current;
    const controller = new AbortController(); requests.current.add(controller);
    try {
      const result = await PCMAdapter.getMcpOAuthClients(tenant, {limit:50,offset,signal:controller.signal});
      if (current() && version === reads.current && !controller.signal.aborted) {
        setData(result); setListError('');
      }
    } catch (failure) {
      if (current() && version === reads.current && !controller.signal.aborted) {
        if (failure.status === 403) invalidate();
        else setListError(text('Cannot load apps. Reload this page.', 'לא ניתן לטעון יישומים. טענו את הדף מחדש.'));
      }
    } finally { requests.current.delete(controller); }
  };
  useEffect(() => {
    alive.current = true;
    const changed = () => { if (!sameAccount(identity.current)) invalidate(); };
    window.addEventListener('storage', changed);
    window.addEventListener('pcm-session-closing', invalidate);
    // Same-document credential changes do not dispatch a storage event.
    const timer = setInterval(changed, 250);
    return () => {
      alive.current = false; ++epoch.current; ++reads.current; abort(); clearInterval(timer);
      window.removeEventListener('storage', changed);
      window.removeEventListener('pcm-session-closing', invalidate);
    };
  }, [tenant]);
  useEffect(() => { setData(null); load(); }, [tenant, offset]);
  useEffect(() => { if (dialog?.secret) heading.current?.focus(); }, [dialog?.secret]);
  useEffect(() => { if (error && dialog) errorRef.current?.focus(); }, [error]);

  const open = (mode, row = null) => {
    if (!current()) { invalidate(); return; }
    ++epoch.current; locked.current = false; setError('');
    setDialog({mode,row,name:row?.name || '',callback:row?.redirect_uri || ''});
  };
  const submit = async event => {
    event.preventDefault();
    if (locked.current || !dialog || dialog.failed || dialog.secret) return;
    if (!current()) { invalidate(); return; }
    locked.current = true; setBusy(true); setError('');
    const version = epoch.current;
    const controller = new AbortController(); requests.current.add(controller);
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const opts = {signal:controller.signal};
      let result;
      if (dialog.mode === 'add') result = await PCMAdapter.createMcpOAuthClient(tenant,
        {name:dialog.name,redirect_uri:dialog.callback},opts);
      else if (dialog.mode === 'rotate') result = await PCMAdapter.rotateMcpOAuthClientSecret(
        tenant,dialog.row.client_id,dialog.row.revision,opts);
      else result = await PCMAdapter.updateMcpOAuthClient(tenant,dialog.row.client_id,{
        revision:dialog.row.revision,name:dialog.mode === 'edit' ? dialog.name : dialog.row.name,
        redirect_uri:dialog.mode === 'edit' ? dialog.callback : dialog.row.redirect_uri,
        enabled:dialog.mode === 'toggle' ? !dialog.row.enabled : dialog.row.enabled,
      },opts);
      if (!current() || version !== epoch.current || controller.signal.aborted) return;
      if (dialog.mode === 'add' || dialog.mode === 'rotate') {
        setDialog({...dialog,secret:result.client_secret,clientId:result.client_id,serverUrl:result.server_url,
          callback:result.redirect_uri}); load();
      } else close();
    } catch (failure) {
      if (!current() || version !== epoch.current) return;
      setDialog({...dialog,failed:true});
      setError(failure.status === 409
        ? text('This app changed. Close and reload before making another change. No replacement secret was issued.',
          'היישום השתנה. סגרו וטענו מחדש לפני שינוי נוסף. לא הונפק סוד חלופי.')
        : failure.status === 422 ? text('Check the app name (1–80 characters) and the exact HTTPS callback. Close and correct the registration; no secret was issued.',
          'בדקו את שם היישום (1–80 תווים) ואת כתובת החזרה המדויקת ב-HTTPS. סגרו ותקנו את הרישום; לא הונפק סוד.')
        : text('The response was lost or refused. The change may have committed. Close and reload the app list; use secret rotation if the secret was lost. Do not repeat creation or rotation.',
          'התשובה אבדה או נדחתה. ייתכן שהשינוי נשמר. סגרו וטענו מחדש את הרשימה; אם הסוד אבד, בצעו סבב סוד. אל תחזרו על יצירה או סבב.'));
    } finally {
      clearTimeout(timer); requests.current.delete(controller);
      if (current() && version === epoch.current) setBusy(false);
    }
  };
  const action = dialog?.mode === 'add' ? text('Create','יצירה') : dialog?.mode === 'rotate'
    ? text('Rotate secret','סבב סוד') : dialog?.mode === 'toggle'
      ? (dialog.row.enabled ? text('Disable app','השבתת יישום') : text('Enable app','הפעלת יישום')) : text('Save','שמירה');
  const title = dialog?.secret ? text('Copy the secret now','העתיקו את הסוד עכשיו') : action;
  const guidance = text('Paste the exact HTTPS callback supplied by the app, including fixed query parameters. No wildcards, fragments, HTTP or custom schemes. PM never fetches this address.',
    'הדביקו את כתובת החזרה המדויקת ב-HTTPS שהיישום מספק, כולל פרמטרי שאילתה קבועים. אין תווים כלליים, מקטעים, HTTP או סכמות מותאמות. PM אינו פונה לכתובת הזו.');
  const setup = text('Copy the MCP URL into the app, then copy its exact callback here. Copy the public client ID and the once-only secret into the app. Enable this registration, connect from the app and sign in with a named account of this tenant. OAuth uses authorization code + S256 PKCE and client_secret_post. If the callback mismatches, copy the exact address again; after disabling or rotation, reconnect.',
    'העתיקו את כתובת MCP ליישום, ואז את כתובת החזרה המדויקת שלו לכאן. העתיקו את המזהה הציבורי ואת הסוד החד-פעמי ליישום. הפעילו את הרישום, התחברו מהיישום והיכנסו עם משתמש רשום של חברה זו. OAuth משתמש בקוד הרשאה עם S256 PKCE וב-client_secret_post. אם כתובת החזרה אינה תואמת, העתיקו שוב את הכתובת המדויקת; אחרי השבתה או סבב התחברו מחדש.');
  const reason = value => ({
    remote_mcp_disabled:text('MCP is disabled for the installation.','MCP מושבת בהתקנה.'),
    oauth_disabled:text('OAuth is disabled for the installation.','OAuth מושבת בהתקנה.'),
    tenant_mcp_disabled:text('MCP is disabled for this tenant.','MCP מושבת לחברה זו.'),
    tenant_inactive:text('This tenant is inactive.','החברה אינה פעילה.'),
    client_disabled:text('This app is disabled.','היישום מושבת.'),
    invalid_client_config:text('App configuration needs repair.','יש לתקן את הגדרות היישום.'),
    invalid_server_config:text('The canonical server configuration needs repair.','יש לתקן את הגדרות השרת הקנוני.'),
  }[value] || value);
  const copy = async (event, value) => {
    const code = event.currentTarget.previousElementSibling;
    try {
      await navigator.clipboard.writeText(value);
      if (current()) setCopyStatus(text('Copied to clipboard.','הועתק ללוח.'));
    } catch (_) {
      if (!current()) return;
      const range = document.createRange(); range.selectNodeContents(code);
      const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
      setCopyStatus(text('Automatic copy is unavailable. The value is selected; use Ctrl+C or Cmd+C.',
        'ההעתקה האוטומטית אינה זמינה. הערך נבחר; השתמשו ב-Ctrl+C או Cmd+C.'));
    }
  };
  const publicValue = (label, value) => <p>{label}: <code dir="ltr">{value || '—'}</code>{value && <button type="button"
    className="btn btn-sm" onClick={e=>copy(e,value)} aria-label={`${text('Copy','העתק')} ${label}`}>{text('Copy','העתק')}</button>}</p>;
  return <section className="card set-section wide" data-mcp-oauth-clients="" dir={he ? 'rtl' : 'ltr'} style={{overflowWrap:'anywhere',minWidth:0}}>
    <h3>{text('MCP OAuth apps','יישומי OAuth ל-MCP')}</h3>
    <p className="set-note">{text(`Tenant #${tenant}`,`חברה #${tenant}`)}</p>
    <p className="set-note">{guidance}</p>
    <p className="set-note">{setup}</p>
    {invalid ? <p role="alert">{text('Account changed. Reload this page.','החשבון השתנה. טענו את הדף מחדש.')}</p> : <>
      <button type="button" className="btn btn-sm" onClick={() => open('add')}>{text('Add app','הוספת יישום')}</button>
      {!dialog && listError && <p className="set-error" role="alert" aria-live="assertive">{listError}</p>}
      {!data && !listError && <p role="status">{text('Loading…','טוען…')}</p>}
      {data?.items.map(row => <article key={row.client_id} data-client-id={row.client_id}>
        <h4 dir="auto">{row.name}</h4>
        {publicValue(text('MCP URL','כתובת MCP'),row.server_url)}
        {publicValue(text('Public client ID','מזהה יישום ציבורי'),row.client_id)}
        {publicValue(text('Callback','כתובת חזרה'),row.redirect_uri)}
        <p>{text('Secret configured','סוד מוגדר')}: {row.has_secret ? text('Yes','כן') : text('No','לא')};{' '}
          {row.enabled ? text('Enabled','פעיל') : text('Disabled','מושבת')};{' '}
          {row.availability.allowed ? text('Available','זמין') : text('Unavailable','לא זמין')}</p>
        <p className="set-note">{row.availability.reasons.map(reason).join(' ')}</p>
        <p>{text('Live connections','חיבורים חיים')}: {row.live_connections} · {text('Last authenticated use','שימוש מאומת אחרון')}: {row.last_used_at || '—'}</p>
        <div className="set-row">
          <button type="button" className="btn btn-sm" onClick={() => open('edit',row)}>{text('Edit','עריכה')}</button>
          <button type="button" className="btn btn-sm" onClick={() => open('toggle',row)}>{row.enabled ? text('Disable app','השבתת יישום') : text('Enable app','הפעלת יישום')}</button>
          <button type="button" className="btn btn-sm danger" onClick={() => open('rotate',row)}>{text('Rotate secret','סבב סוד')}</button>
        </div>
      </article>)}
      <p role="status" aria-live="polite">{copyStatus}</p>
      {data && <div className="set-row">
        <button type="button" className="btn btn-sm" disabled={offset===0} onClick={()=>setOffset(offset-50)}>{text('Previous','הקודם')}</button>
        <span role="status">{Math.min(offset+1,data.total)}–{Math.min(offset+50,data.total)} / {data.total}</span>
        <button type="button" className="btn btn-sm" disabled={offset+50>=data.total} onClick={()=>setOffset(offset+50)}>{text('Next','הבא')}</button>
      </div>}
    </>}
    {dialog && <Modal label={title} dir={he ? 'rtl' : 'ltr'} onClose={close}>
      <h2 tabIndex={-1} ref={heading}>{title}</h2>
      <p>{text(`Tenant #${tenant}`,`חברה #${tenant}`)}{dialog.row && <> · <span dir="auto">{dialog.row.name}</span> · {text('revision','גרסה')} {dialog.row.revision}</>}</p>
      <form onSubmit={submit}>
        {!dialog.secret && (dialog.mode==='add' || dialog.mode==='edit') && <fieldset disabled={busy || dialog.failed}>
          <div className="set-row"><label htmlFor={`${id}-name`}>{text('App name','שם היישום')}</label>
            <input className="set-input" id={`${id}-name`} value={dialog.name} required maxLength={80} dir="auto"
              onChange={e=>setDialog({...dialog,name:e.target.value})}/></div>
          <div className="set-row"><label htmlFor={`${id}-callback`}>{text('Exact HTTPS callback','כתובת חזרה מדויקת ב-HTTPS')}</label>
            <input className="set-input" id={`${id}-callback`} value={dialog.callback} required maxLength={2048} type="url" dir="ltr"
              aria-describedby={`${id}-guidance`} onChange={e=>setDialog({...dialog,callback:e.target.value})}/></div>
          <p className="set-note" id={`${id}-guidance`}>{guidance}</p>
        </fieldset>}
        {!dialog.secret && (dialog.mode==='rotate' || dialog.mode==='toggle') && <p className="set-note">
          {text(`Affected live connections: ${dialog.row.live_connections}. Rotation or disabling disconnects this app only. Owners must reconnect with the current secret after it is enabled; re-enabling does not restore revoked connections.`,
            `חיבורים חיים מושפעים: ${dialog.row.live_connections}. סבב או השבתה מנתקים רק יישום זה. המשתמשים צריכים להתחבר מחדש עם הסוד העדכני אחרי הפעלה; הפעלה אינה משחזרת חיבורים שבוטלו.`)}
          {' '}{text('The count is a snapshot; the action applies to every connection live at confirmation.',
            'המספר הוא תמונת מצב; הפעולה חלה על כל חיבור חי בעת האישור.')}
        </p>}
        {dialog.secret && <><p className="set-note">{setup}</p>
          {publicValue(text('MCP URL','כתובת MCP'),dialog.serverUrl)}
          {publicValue(text('Public client ID','מזהה יישום ציבורי'),dialog.clientId)}
          {publicValue(text('Callback','כתובת חזרה'),dialog.callback)}
          <p role="status" aria-live="polite">{copyStatus}</p>
          <window.OnceBox label="client_secret" value={dialog.secret} lang={lang} note={guidance}/></>}
        {error && <p tabIndex={-1} ref={errorRef} className="set-error" role="alert" aria-live="assertive">{error}</p>}
        <div className="dlg-actions">
          <button type="button" className="btn btn-sm" onClick={close}>{text('Close','סגירה')}</button>
          {!dialog.secret && <button type="submit" className="btn btn-sm primary" disabled={busy || dialog.failed}>{busy ? text('Saving…','שומר…') : action}</button>}
        </div>
      </form>
    </Modal>}
  </section>;
}

export { McpOAuthClients };
